import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ShieldCheck, Loader2, Building2, User, CheckCircle2, RefreshCw, Circle, PhoneCall, Sparkles, MapPin, AlertTriangle, PartyPopper, Bot, ListChecks, Settings2, PhoneOutgoing, Upload, FileText, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BUSINESS_TYPES = [
  "Limited Company (Ltd)", "Sole Trader", "Partnership", "Limited Liability Partnership (LLP)",
  "Public Limited Company (PLC)", "Charity / Non-profit", "Other",
];
const ID_DOC_TYPES = [
  { value: "passport", label: "Passport" },
  { value: "government_issued_document", label: "Driving licence / National ID" },
];
const ADDRESS_DOC_TYPES = [
  { value: "utility_bill", label: "Utility bill (gas, electric, water)" },
  { value: "tax_document", label: "Council tax bill" },
  { value: "rental_receipt", label: "Tenancy agreement / rental receipt" },
];

const UK_AREAS = [
  { code: "20", label: "London (020)" },
  { code: "121", label: "Birmingham (0121)" },
  { code: "161", label: "Manchester (0161)" },
  { code: "113", label: "Leeds (0113)" },
  { code: "151", label: "Liverpool (0151)" },
  { code: "117", label: "Bristol (0117)" },
  { code: "131", label: "Edinburgh (0131)" },
  { code: "141", label: "Glasgow (0141)" },
  { code: "29", label: "Cardiff (029)" },
];

function Timeline({ stages }) {
  return (
    <div data-testid="progress-tracker" className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="font-heading font-bold tracking-tight mb-4">Setup progress</h2>
      <ol className="relative">
        {stages.map((s, i) => {
          const last = i === stages.length - 1;
          return (
            <li key={s.key} data-testid={`stage-${s.key}`} className="relative flex gap-4 pb-6 last:pb-0">
              {!last && <span className={`absolute left-[13px] top-7 h-full w-0.5 ${s.done ? "bg-emerald-400" : "bg-slate-200"}`} />}
              <span className="relative z-10 mt-0.5">
                {s.done ? <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                  : s.current ? <Loader2 className="h-7 w-7 text-blue-500 animate-spin" />
                  : <Circle className="h-7 w-7 text-slate-300" />}
              </span>
              <div className="flex-1 pt-0.5">
                <p className={`font-semibold text-sm ${s.done ? "text-slate-900" : s.current ? "text-blue-700" : "text-slate-400"}`}>{s.label}</p>
                {s.current && s.note && <p className="mt-1 text-sm text-slate-500" data-testid={`stage-note-${s.key}`}>{s.note}</p>}
                {!s.current && s.note && <p className="mt-1 text-sm text-amber-600" data-testid={`stage-note-${s.key}`}>{s.note}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function AreaPicker({ value, onChange }) {
  return (
    <div>
      <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Preferred area / city for your number</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {UK_AREAS.map((a) => (
          <button key={a.code} type="button" data-testid={`area-${a.code}`} onClick={() => onChange(a.code)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${value === a.code ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {a.label}
          </button>
        ))}
      </div>
      <Input data-testid="area-custom" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} placeholder="Or type an area code, e.g. 1273" className="mt-2 max-w-xs" />
    </div>
  );
}

export default function PhoneSetup() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("business");
  const [form, setForm] = useState({
    name: "", business_type: "", registration_number: "", website: "", business_phone: "",
    first_name: "", last_name: "", job_title: "", email: "", phone: "",
    street: "", city: "", region: "", postal_code: "", country: "United Kingdom",
    id_doc_type: "passport", address_doc_type: "utility_bill", preferred_area_code: "20",
  });
  const [idFile, setIdFile] = useState(null);
  const [addrFile, setAddrFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [twilioErrors, setTwilioErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(false);
  const wasLive = useRef(false);

  const load = () => api.get("/verification").then((r) => {
    if (r.data?.is_live && !wasLive.current) { wasLive.current = true; window.dispatchEvent(new Event("notif-refresh")); }
    setState(r.data); setLoading(false);
  }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Auto-refresh while awaiting activation so the status updates on its own.
  useEffect(() => {
    const s = state?.status;
    const pending = (s === "under_review" || s === "pending") || (s === "verified" && !state?.purchased_number);
    if (!pending) return;
    const iv = setInterval(() => { load(); window.dispatchEvent(new Event("notif-refresh")); }, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.status, state?.purchased_number]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setFieldErrors((e) => ({ ...e, [k]: undefined })); };

  const submit = async () => {
    setBusy(true);
    setFieldErrors({});
    setTwilioErrors([]);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ entity_type: entity, ...form }));
      if (entity === "individual") {
        if (idFile) fd.append("id_document", idFile);
        if (addrFile) fd.append("address_document", addrFile);
      }
      await api.post("/verification/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Details submitted — we'll take it from here.");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") {
        if (d.field_errors) { setFieldErrors(d.field_errors); toast.error(d.message || "Please fix the highlighted fields."); }
        else if (d.twilio_errors) { setTwilioErrors(d.twilio_errors); toast.error(d.message || "Verification could not be completed."); }
        else { toast.error(d.message || "Could not submit. Please check your details."); }
      } else {
        toast.error(typeof d === "string" ? d : "Could not submit. Please check your details.");
      }
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post("/verification/refresh"); setState(r.data); toast.info("Status refreshed."); }
    catch { toast.error("Could not refresh."); }
    finally { setBusy(false); }
  };

  const changeArea = async (code) => {
    setBusy(true);
    try { const r = await api.post("/verification/preferred-area", { area_code: code }); setState(r.data); toast.success("Area updated — activating your number."); }
    catch { toast.error("Could not update area."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="py-24 text-center text-slate-400" data-testid="phone-loading"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  const status = state?.status || "not_started";
  const showForm = status === "not_started" || status === "action_needed";
  const active = !!state?.purchased_number;

  return (
    <div data-testid="phone-setup-view" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Phone className="h-6 w-6 text-blue-600" /> Phone Number</h1>
        <p className="text-slate-500 mt-1">Get a dedicated phone number for your AI receptionist. We handle everything automatically.</p>
      </div>

      {/* Celebration when live */}
      {active && state.is_live && (
        <div data-testid="celebration-card" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-blue-50 p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500 flex items-center justify-center mb-4"><PartyPopper className="h-7 w-7 text-white" /></div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">🎉 Your OperaMind phone number is now live!</h2>
          <p className="font-mono text-2xl font-bold text-blue-700 mt-3" data-testid="celebration-number">{state.purchased_number}</p>
          <div className="mt-3 flex items-center justify-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Active</span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600"><ShieldCheck className="h-4 w-4" /> AI Receptionist Connected</span>
          </div>
          <p className="text-slate-500 mt-3">You can now start receiving calls.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a href={`tel:${(state.purchased_number || "").replace(/\s/g, "")}`} data-testid="call-my-number"><Button className="bg-blue-600 hover:bg-blue-700 gap-2"><PhoneOutgoing className="h-4 w-4" /> Call my number</Button></a>
            <Button data-testid="view-ai-btn" variant="outline" onClick={() => navigate("/dashboard/ai-receptionist")} className="gap-2"><Bot className="h-4 w-4" /> View AI receptionist</Button>
            <Button data-testid="view-calls-btn" variant="outline" onClick={() => navigate("/dashboard/calls")} className="gap-2"><ListChecks className="h-4 w-4" /> View call logs</Button>
            <Button data-testid="manage-number-btn" variant="outline" onClick={() => setManage((m) => !m)} className="gap-2"><Settings2 className="h-4 w-4" /> Manage phone number</Button>
          </div>
        </div>
      )}

      {/* Active number details (always when connecting; toggle when live) */}
      {active && (!state.is_live || manage) && (
        <div data-testid="active-number-card" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-xl bg-emerald-500 flex items-center justify-center"><PhoneCall className="h-5 w-5 text-white" /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Your AI receptionist number</p>
              <p className="font-mono text-2xl font-bold text-slate-900" data-testid="active-number">{state.purchased_number}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Detail label="Current status" value={state.is_live ? "Active" : "Activating"} valueClass={state.is_live ? "text-emerald-600" : "text-amber-600"} />
            <Detail label="Number type" value={state.number_type || "UK Local (Voice)"} />
            <Detail label="AI receptionist" value={state.number_connected ? "Connected" : "Connecting…"} valueClass={state.number_connected ? "text-emerald-600" : "text-amber-600"} />
            <Detail label="Date activated" value={state.activated_at ? new Date(state.activated_at).toLocaleDateString() : "—"} />
            <Detail label="Minutes used" value={state.minutes_used ?? 0} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="change-number-btn" variant="outline" size="sm" disabled className="text-xs">Change number (soon)</Button>
            <Button data-testid="port-number-btn" variant="outline" size="sm" disabled className="text-xs">Port a number (soon)</Button>
            <Button data-testid="forward-number-btn" variant="outline" size="sm" disabled className="text-xs">Forward existing (soon)</Button>
          </div>
        </div>
      )}

      {/* Progress tracker (any submission that isn't active yet) */}
      {!showForm && !active && state?.stages && (
        <>
          {(status === "under_review" || status === "pending") && !state.number_search_failed && (
            <div data-testid="reassurance-message" className="rounded-2xl border border-blue-200 bg-blue-50 p-5 flex gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900">Estimated activation: 1–3 business days</p>
                <p className="text-sm text-blue-700 mt-1">Your verification is currently being reviewed by our phone provider. This usually takes between 1 and 3 business days. We'll automatically activate your phone number as soon as it's approved and notify you immediately.</p>
              </div>
            </div>
          )}
          <Timeline stages={state.stages} />
          {state.number_search_failed ? (
            <div data-testid="area-retry" className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-amber-600" /><h2 className="font-heading font-bold tracking-tight text-amber-800">Choose another area</h2></div>
              <p className="text-sm text-amber-700 mb-3">No number was available in your chosen area. Pick another and we'll activate it right away.</p>
              <AreaPicker value={form.preferred_area_code} onChange={(c) => set("preferred_area_code", c)} />
              <Button data-testid="retry-area-btn" onClick={() => changeArea(form.preferred_area_code)} disabled={busy} className="mt-3 bg-blue-600 hover:bg-blue-700 gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Activate my number
              </Button>
            </div>
          ) : (status === "under_review" || status === "pending") ? (
            <div className="flex justify-end">
              <Button data-testid="verification-refresh" onClick={refresh} disabled={busy} variant="outline" size="sm" className="gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Check status
              </Button>
            </div>
          ) : null}
        </>
      )}

      {/* Submission form */}
      {showForm && (
        <div data-testid="verification-form" className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold tracking-tight">Verification details</h2>
          <p className="text-sm text-slate-500 mt-1">To register a UK phone number, our provider (Twilio) requires these details by law. They're used only to verify and register your number.</p>

          {status === "action_needed" && (twilioErrors.length > 0 || state?.verification_error) && (
            <div data-testid="verification-error-banner" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-red-600" /><p className="text-sm font-semibold text-red-800">Please correct the following and resubmit</p></div>
              <ul className="list-disc pl-5 space-y-1">
                {(twilioErrors.length ? twilioErrors : String(state?.verification_error || "").split("; ").filter(Boolean)).map((er, i) => (
                  <li key={i} className="text-sm text-red-700" data-testid={`verify-err-${i}`}>{er}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button data-testid="entity-business" onClick={() => { setEntity("business"); setFieldErrors({}); setTwilioErrors([]); }} className={`flex items-center gap-2 rounded-xl border p-4 text-left transition-colors ${entity === "business" ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
              <Building2 className="h-5 w-5 text-blue-600" /><div><p className="font-semibold text-sm">Business</p><p className="text-xs text-slate-500">Registered company or sole trader</p></div>
            </button>
            <button data-testid="entity-individual" onClick={() => { setEntity("individual"); setFieldErrors({}); setTwilioErrors([]); }} className={`flex items-center gap-2 rounded-xl border p-4 text-left transition-colors ${entity === "individual" ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
              <User className="h-5 w-5 text-blue-600" /><div><p className="font-semibold text-sm">Individual</p><p className="text-xs text-slate-500">Personal / self-employed</p></div>
            </button>
          </div>

          {entity === "business" && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Business</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField className="sm:col-span-2" label="Legal business name *" testid="v-name" value={form.name} onChange={(e) => set("name", e.target.value)} error={fieldErrors.name} placeholder="Acme Salons Ltd" />
                <TextField label="Company registration number (CRN) *" testid="v-reg" value={form.registration_number} onChange={(e) => set("registration_number", e.target.value)} error={fieldErrors.registration_number} placeholder="12345678" />
                <div>
                  <Label className="text-xs">Business type</Label>
                  <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
                    <SelectTrigger data-testid="v-biztype" className="mt-1"><SelectValue placeholder="Select business type" /></SelectTrigger>
                    <SelectContent>{BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <TextField label="Business website *" testid="v-website" value={form.website} onChange={(e) => set("website", e.target.value)} error={fieldErrors.website} placeholder="https://yourbusiness.co.uk" />
                <TextField label="Business phone number" testid="v-bizphone" value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)} error={fieldErrors.business_phone} placeholder="+44 20 7946 0000" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 mt-5">Authorised contact</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="First name *" testid="v-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} error={fieldErrors.first_name} />
                <TextField label="Last name *" testid="v-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} error={fieldErrors.last_name} />
                <TextField label="Job title" testid="v-jobtitle" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} error={fieldErrors.job_title} placeholder="Owner / Director" />
                <TextField label="Company email *" testid="v-email" value={form.email} onChange={(e) => set("email", e.target.value)} error={fieldErrors.email} placeholder="you@yourbusiness.co.uk" />
                <TextField className="sm:col-span-2" label="Mobile phone number *" testid="v-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} error={fieldErrors.phone} placeholder="+44 7700 900000" />
              </div>
            </div>
          )}

          {entity === "individual" && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Your details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="First name *" testid="v-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} error={fieldErrors.first_name} />
                <TextField label="Last name *" testid="v-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} error={fieldErrors.last_name} />
                <TextField label="Email *" testid="v-email" value={form.email} onChange={(e) => set("email", e.target.value)} error={fieldErrors.email} />
                <TextField label="Mobile phone number *" testid="v-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} error={fieldErrors.phone} placeholder="+44 7700 900000" />
              </div>
            </div>
          )}

          {/* Address (both) */}
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 mt-5">Address</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField className="sm:col-span-2" label="Street address *" testid="v-street" value={form.street} onChange={(e) => set("street", e.target.value)} error={fieldErrors.street} placeholder="123 High Street" />
            <TextField label="City / town *" testid="v-city" value={form.city} onChange={(e) => set("city", e.target.value)} error={fieldErrors.city} />
            <TextField label="County" testid="v-region" value={form.region} onChange={(e) => set("region", e.target.value)} error={fieldErrors.region} />
            <TextField label="Postcode *" testid="v-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} error={fieldErrors.postal_code} placeholder="SW1A 1AA" />
            <div>
              <Label className="text-xs">Country</Label>
              <Input data-testid="v-country" value={form.country} disabled className="mt-1 bg-slate-50 text-slate-500" />
            </div>
          </div>

          {/* Individual document uploads */}
          {entity === "individual" && (
            <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
              <div className="flex items-center gap-2 mb-1"><FileText className="h-4 w-4 text-blue-600" /><p className="text-sm font-semibold text-slate-800">Identity & address documents</p></div>
              <p className="text-xs text-slate-500 mb-3">Twilio requires proof of identity and proof of address for individuals. Upload clear JPG, PNG or PDF files (max 5&nbsp;MB each).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Identity document type</Label>
                  <Select value={form.id_doc_type} onValueChange={(v) => set("id_doc_type", v)}>
                    <SelectTrigger data-testid="v-idtype" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ID_DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <FileField testid="v-id-file" file={idFile} onFile={(f) => { setIdFile(f); setFieldErrors((e) => ({ ...e, id_document: undefined })); }} error={fieldErrors.id_document} />
                </div>
                <div>
                  <Label className="text-xs">Proof of address type</Label>
                  <Select value={form.address_doc_type} onValueChange={(v) => set("address_doc_type", v)}>
                    <SelectTrigger data-testid="v-addrtype" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ADDRESS_DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <FileField testid="v-addr-file" file={addrFile} onFile={(f) => { setAddrFile(f); setFieldErrors((e) => ({ ...e, address_document: undefined })); }} error={fieldErrors.address_document} />
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <AreaPicker value={form.preferred_area_code} onChange={(c) => set("preferred_area_code", c)} />
            <p className="mt-2 text-xs text-slate-400">Once approved, we'll automatically activate the first available number in this area.</p>
          </div>

          <Button data-testid="verification-submit" onClick={submit} disabled={busy} className="mt-5 bg-blue-600 hover:bg-blue-700 gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Submit for verification
          </Button>
        </div>
      )}
    </div>
  );
}

function TextField({ label, testid, value, onChange, error, placeholder, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} value={value} onChange={onChange} placeholder={placeholder}
        className={`mt-1 ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
      {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function FileField({ testid, file, onFile, error }) {
  return (
    <div className="mt-2">
      <label className={`flex items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 cursor-pointer transition-colors ${error ? "border-red-400 bg-red-50/40" : "border-slate-300 hover:border-blue-400 bg-white"}`}>
        <Upload className="h-4 w-4 text-slate-400 shrink-0" />
        <span className="text-xs text-slate-600 truncate flex-1">{file ? file.name : "Choose file (JPG, PNG, PDF)"}</span>
        {file && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
        <input data-testid={testid} type="file" accept="image/jpeg,image/png,application/pdf" className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] || null)} />
      </label>
      {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function Detail({ label, value, valueClass = "text-slate-900" }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-semibold ${valueClass}`}>{value}</p>
    </div>
  );
}
